/**
 * CAOS-572 / CAOS-571 — drawing a placement, and printing the same placement as code.
 *
 * A placement is a piece with values set on it, holding other pieces. Two things are done with one:
 * it is BUILT into real elements, and it is PRINTED as the code a reader copies. Both come out of
 * the same tree, walked twice, which is the property the whole component reference rests on — the
 * preview cannot drift from the code, because the code is what made the preview.
 *
 * CAOS-571 — printed does not mean valid. The first version of this file printed a shape that LOOKED
 * like a stored page placement — `type`, `key`, `inputs` — but spelled `key` as the element's hyphenated
 * tag (`kernel/src/meta/shared.ts`'s `ComponentRef` allows no hyphen) and, for a node with children or
 * a plain-markup root, printed a `type` and a `children` field the real schema has no member for at
 * all. Pasted onto a real page, that JSON would fail deploy validation on sight — which means the
 * page's own claim, "authorable metadata that can be copied onto a real page and behave identically",
 * was false for exactly the examples that used it. {@link placementGap} names the two cases the page
 * model genuinely cannot store yet; {@link placementSectionJson} prints only what is left, and prints
 * it in the schema's own shape rather than one invented to look like it.
 *
 * CAOS-654 — what a piece holds is storable now: a stored placement carries `children` (text, or
 * further placed pieces, each naming the `slot` of its parent it goes into). So what {@link placementGap}
 * still names is bare markup anywhere in the tree, and a slot on a piece placed directly in a section,
 * which has no parent whose slot it could go into.
 *
 * Nothing here knows the name of any component. It knows what a placement IS.
 */
import type { PlacementNode } from './component-docs.js';

/**
 * ATTRIBUTE OR PROPERTY — one rule, stated once, applied everywhere.
 *
 * A string, a number or a boolean is written as an ATTRIBUTE, because that is the form a page
 * author writes in markup and the form a stored placement round-trips through. Anything else — a
 * list of options, a set of rows, an injected search — is set as a PROPERTY, because an attribute
 * is a string and there is no honest way to put a record set in one.
 *
 * A boolean is the one that catches people: `true` writes the attribute EMPTY (`disabled=""`, which
 * is how HTML says a flag is set) and `false` omits it entirely, because an attribute reading
 * `disabled="false"` is still a set attribute and still disables the control.
 */
export function isAttributeValue(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

/** Build one placement into the real element, configured, with its children inside it. */
export function buildPlacement(node: PlacementNode): HTMLElement {
  const element = document.createElement(node.component);
  // Which of the parent's named slots this goes into. Set here rather than through `inputs`,
  // because it is the PARENT's contract and not this piece's.
  if (node.slot !== undefined) element.setAttribute('slot', node.slot);
  for (const [name, value] of Object.entries(node.inputs ?? {})) {
    if (value === undefined) continue;
    if (typeof value === 'boolean') {
      if (value) element.setAttribute(attributeName(name), '');
      continue;
    }
    if (isAttributeValue(value)) element.setAttribute(attributeName(name), String(value));
    else (element as unknown as Record<string, unknown>)[name] = value;
  }
  for (const child of node.children ?? []) {
    element.append(typeof child === 'string' ? document.createTextNode(child) : buildPlacement(child));
  }
  return element;
}

/**
 * The attribute spelling of an input name.
 *
 * An input is declared under its PROPERTY name, because that is the contract a stored placement
 * writes under; markup wants the hyphenated form. A name already carrying a hyphen is left exactly
 * as it is, so a declaration may spell either and mean the same input.
 */
export function attributeName(name: string): string {
  return name.includes('-') ? name : name.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}

// ── Printing it ───────────────────────────────────────────────────────────────────────────────────

/** One part of a code panel: a file tab and what is under it. */
export interface CodePart {
  /** What the tab says. */
  readonly name: string;
  /** The language, for highlighting. */
  readonly language: 'json' | 'html' | 'js';
  readonly text: string;
}

/**
 * A custom element, as opposed to ordinary markup.
 *
 * The whole rule is the hyphen — it is what HTML itself uses to tell a custom element from a built-in
 * one, so there is nothing to invent and nothing that can disagree with the browser.
 */
export function isComponentTag(tag: string): boolean {
  return tag.includes('-');
}

/**
 * Why THIS node cannot be printed as a real page placement, named specifically enough to act on —
 * or `undefined` when it can.
 *
 * A stored page item (`kernel/src/meta/page.ts` `ComponentInstanceSchema`) is `id`, `type:
 * "component"`, `key` and an `inputs` map — a closed, `.strict()` shape with no field for anything a
 * placed piece HOLDS and no kind of item other than a field or a component. Two things a declared
 * example can say that the schema has no way to store:
 *
 *   • the node is not a component at all — ordinary markup, placed nowhere a page item can be;
 *   • something inside it is bare markup — a placement holds text and placed pieces, never markup;
 *   • a piece of text inside it is empty, which the stored shape refuses;
 *   • the node names a slot while placed directly in a section, which has no slots to name.
 *
 * Both are found here rather than papered over with an invented key the schema does not have, because
 * printing something that LOOKS like metadata and is not valid metadata is worse than printing
 * nothing: it teaches a shape that fails the moment somebody pastes it and deploys it.
 */
export function placementGap(node: PlacementNode): string | undefined {
  if (slotOf(node) !== undefined) {
    return `<${node.component}> names a slot, and a piece placed directly in a page section has no parent whose slot it could go into.`;
  }
  return gapWithin(node);
}

/** The first thing in a placement, at any depth, that a stored page placement cannot hold. */
function gapWithin(node: PlacementNode): string | undefined {
  if (!isComponentTag(node.component)) {
    return `<${node.component}> is ordinary markup, not an installable component — a page places components, never bare markup.`;
  }
  // CAOS-1568 — a slot is the NAME of one of the parent's slots, and a name is a string. Written as
  // a number or a flag it is neither a slot nor one of the piece's own inputs: the element would
  // still be given the attribute while the placement dropped it, which is the one thing this file
  // exists to prevent — the code and the preview beside it disagreeing.
  const writtenSlot = node.inputs?.['slot'];
  if (writtenSlot !== undefined && typeof writtenSlot !== 'string') {
    return `<${node.component}> is given \`slot\` as ${typeof writtenSlot}, and a slot is the name of one of the parent's slots. A placement carries that name, so this would show in the preview and be missing from the code beside it.`;
  }
  // CAOS-1568 — an ARIA or data attribute is set on the ELEMENT and is not among the inputs the
  // component declares, so `inputs` has nowhere to put it and the deploy gate refuses a placement
  // that names it. Printing one anyway would promise a page that behaves like the example when it
  // would in fact be placed without the very attribute the example is about.
  //
  // Only these two prefixes, and deliberately not "every global HTML attribute", which is the wider
  // rule this looks like. Measured against the shipped library: no component declares an input
  // beginning `aria_` or `data_`, so these two can never swallow a real one — but `title` IS a
  // declared input on a shipped component, so a rule phrased over the HTML globals would refuse to
  // print a placement for an input that genuinely exists. Anything element-level beyond these is
  // caught where the knowledge to catch it lives: the check that resolves every printed input
  // against the inputs its component declares. Nothing in this file knows any component's name.
  for (const name of Object.keys(node.inputs ?? {})) {
    if (name === 'slot') continue;
    if (/^(aria|data)-/.test(name)) {
      return `<${node.component}> is given \`${name}\`, which is set on the element rather than declared as an input. A stored placement carries only a component's own inputs, so a page copying this would place the piece without it.`;
    }
  }
  for (const child of node.children ?? []) {
    if (typeof child === 'string') {
      if (child === '') return `<${node.component}> holds an empty piece of text — text inside a placement has to say something.`;
      continue;
    }
    const inner = gapWithin(child);
    if (inner !== undefined) return inner;
  }
  return undefined;
}

/**
 * The component KEY a stored placement names — the key the package that ships the piece installs it
 * under, which is the only key a page can name and have resolve.
 *
 * A page item's `key` is a `ComponentRef` (`kernel/src/meta/shared.ts` `^[a-z][a-z0-9_]*$`,
 * dot-joined), so it cannot carry the hyphen an element tag is written with. It also cannot carry the
 * element's `caos-` prefix, and that is the part this used to get wrong. The library derives a
 * component's key from its NAME — `AppLauncher` becomes `app_launcher` — while the prefix belongs to
 * the custom-element namespace and never reaches the metadata. So `caos-layout` is installed as
 * `layout`, and a placement printed as `caos_layout` was refused at deploy as a reference to a
 * component no org has (CAOS-1568).
 *
 * The rule is not a guess about one piece: stripping the prefix reproduces the installed key for every
 * tagged piece in the library, and a check in the kernel suite holds each printed placement to the
 * manifests an org actually installs, so this cannot drift back unnoticed.
 */
export function placementKey(tag: string): string {
  return tag.replace(/^caos-/, '').replace(/-/g, '_');
}

/**
 * The INPUT NAME a stored placement sets — the name the component's own manifest declares.
 *
 * An input is written in documentation under the camelCase property the piece observes
 * (`horizontalAlign`) and shipped in the manifest under a bare lower_snake identifier
 * (`horizontal_align`), because `InputName` follows the same rule a component key does. The deploy
 * gate holds a placement to the manifest, so a placement setting the camelCase spelling is refused as
 * an input the component does not declare — which is what the reference printed for every example
 * that set one.
 *
 * This is the transform the manifest generator already applies, repeated here rather than imported
 * because that generator is a build script and this is shipped source. The kernel check compares the
 * printed names against the shipped manifests, so the two staying in step is asserted rather than
 * assumed.
 */
export function placementInputName(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .replace(/-/g, '_')
    .toLowerCase();
}

/** The values set on a piece, keyed the way its manifest declares them. */
function placementInputs(inputs: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(inputs)) {
    // `slot` is not one of the values set on a piece. It is the parent's contract, and a stored
    // placement carries it beside `key` rather than inside `inputs` — so an example that writes it
    // the way markup does still prints as the item field. No component in the library declares an
    // input by that name, which is what makes lifting it unambiguous rather than a guess.
    if (name === 'slot') continue;
    out[placementInputName(name)] = value;
  }
  return out;
}

/** The named slot a piece goes into, whether the example declared it or wrote it as the attribute. */
function slotOf(node: PlacementNode): string | undefined {
  if (node.slot !== undefined) return node.slot;
  const written = node.inputs?.['slot'];
  return typeof written === 'string' ? written : undefined;
}

/**
 * The PLACEMENT view — the authorable metadata, as a page section holding the pieces.
 *
 * This is what somebody building a page copies, and it is meant to be pasted onto a real page and
 * behave identically: the exact shape `kernel/src/meta/page.ts` validates a page's `sections` against
 * (`id` / `section` / `columns` / `items`, each item `id` / `type: "component"` / `key` / `inputs`).
 * Nothing about it is a sample somebody wrote beside the component — it is built from the same nodes
 * the preview above it was built from, so it cannot describe an example the preview does not draw.
 *
 * Only called once every node has passed {@link placementGap} — a node that has not is not printed
 * here at all, in any form; see {@link placementGap} for why.
 */
export function placementSectionJson(nodes: readonly PlacementNode[], sectionLabel: string): string {
  // CAOS-654 — every placement in the example gets its own instance id, numbered in document order (a
  // piece before what it holds), because ids share one namespace on a page. A childless piece placed
  // directly in the section keeps the id it always had.
  let counter = 0;
  const placed = (node: PlacementNode, nested: boolean): Record<string, unknown> => {
    counter += 1;
    const key = placementKey(node.component);
    const id = `${key}_${counter}`;
    const inputs = node.inputs ? placementInputs(node.inputs) : {};
    const slot = slotOf(node);
    return {
      id,
      type: 'component',
      key,
      ...(Object.keys(inputs).length > 0 ? { inputs } : {}),
      // A slot exists only on a piece placed inside another; a section has no slots to name.
      ...(nested && slot !== undefined ? { slot } : {}),
      ...(node.children && node.children.length > 0
        ? { children: node.children.map((child) => (typeof child === 'string' ? child : placed(child, true))) }
        : {}),
    };
  };
  const items = nodes.map((node) => placed(node, false));
  return JSON.stringify({ id: 'example', section: sectionLabel, columns: 1, items }, replacer, 2);
}

/**
 * A function reached the printer.
 *
 * That happens for the one input kind a placement genuinely cannot carry — an injected seam, such
 * as the search a lookup field is given. `JSON.stringify` drops a function silently, which would
 * print a placement missing the very thing the example is demonstrating. It is named instead.
 */
function replacer(_key: string, value: unknown): unknown {
  return typeof value === 'function' ? '«supplied by the surrounding page»' : value;
}

/**
 * The MARKUP view — the piece as somebody writing their own component composes it.
 *
 * Comes back as one or two parts. The elements and their attributes are always the first; when an
 * example sets values an attribute cannot hold, the data it is given is the second, because those
 * are two different things a reader does in two different places. The incumbent's reference does the
 * same with file tabs and it is the right shape.
 */
export function markupParts(nodes: readonly PlacementNode[]): readonly CodePart[] {
  const out: Assignments = { lines: [], handles: [] };
  const html = nodes.map((node, index) => markupOf(node, 0, out, `${localName(node)}${index + 1}`)).join('\n');
  const parts: CodePart[] = [{ name: 'markup.html', language: 'html', text: html }];
  if (out.lines.length > 0) {
    parts.push({
      name: 'data.js',
      language: 'js',
      text: [
        '// What an attribute cannot hold, set as a property — the same values the preview is drawn with.',
        ...out.handles.map((h) => `const ${h} = document.getElementById('${h}');`),
        ...out.lines,
      ].join('\n'),
    });
  }
  return parts;
}

/**
 * The whole of one view as a single pasteable block.
 *
 * This is what a copy control hands over, and the reason it is the WHOLE view rather than the file
 * you happen to be reading: an example that sets a list of rows is two parts, and copying only the
 * markup gives you an element that draws nothing. A copyable example that does not run when pasted
 * is worse than no copy control at all, so the copy is always the runnable whole and the file tabs
 * are for reading.
 *
 * Each element that takes values an attribute cannot hold carries an `id` in the markup, and the
 * script looks it up by that id — so the two halves are joined by something real rather than by the
 * reader working out which element the assignment meant.
 */
export function runnableText(parts: readonly CodePart[]): string {
  if (parts.length === 1) return parts[0]!.text;
  return parts
    .map((part) => (part.language === 'html' ? part.text : `<script>\n${part.text}\n</script>`))
    .join('\n\n');
}

interface Assignments {
  readonly lines: string[];
  readonly handles: string[];
}

const localName = (node: PlacementNode): string => {
  const bare = node.component.replace(/^caos-/, '');
  return bare.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
};

function markupOf(node: PlacementNode, depth: number, out: Assignments, ref: string): string {
  const pad = '  '.repeat(depth);
  const attrs: string[] = [];
  const own: string[] = [];
  if (node.slot !== undefined) attrs.push(`slot="${escapeAttribute(node.slot)}"`);
  for (const [name, value] of Object.entries(node.inputs ?? {})) {
    if (value === undefined) continue;
    if (typeof value === 'boolean') {
      if (value) attrs.push(attributeName(name));
      continue;
    }
    if (isAttributeValue(value)) attrs.push(`${attributeName(name)}="${escapeAttribute(String(value))}"`);
    else if (typeof value === 'function') own.push(`${ref}.${name} = /* supplied by the surrounding page */;`);
    else own.push(`${ref}.${name} = ${JSON.stringify(value, null, 2)};`);
  }
  if (own.length > 0) {
    // The element the script has to find. Written into the markup so a pasted snippet joins its two
    // halves by something real, rather than leaving the reader to work out which element was meant.
    attrs.unshift(`id="${ref}"`);
    out.handles.push(ref);
    out.lines.push(...own);
  }
  const open = attrs.length === 0 ? node.component : `${node.component} ${attrs.join(' ')}`;
  const children = node.children ?? [];
  if (children.length === 0) return `${pad}<${open}></${node.component}>`;
  const onlyText = children.every((c) => typeof c === 'string');
  if (onlyText) return `${pad}<${open}>${children.join('')}</${node.component}>`;
  const inner = children
    .map((child, index) =>
      typeof child === 'string'
        ? `${'  '.repeat(depth + 1)}${child}`
        : markupOf(child, depth + 1, out, `${ref}${localName(child)}${index + 1}`),
    )
    .join('\n');
  return `${pad}<${open}>\n${inner}\n${pad}</${node.component}>`;
}

const escapeAttribute = (value: string): string => value.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
